import { useState, type KeyboardEvent } from 'react';
import { countWords, saveStatus, splitName, type DocMeta } from '../core/documents';
import { useApp, useCloudAuth, useCloudSync, useSession, useUi } from '../app/context';
import { notify, openAccount } from '../app/ui';
import type { CloudDocState } from '../core/cloud';
import { shortcutHint } from './keys';
import { MenuItem, menuKeys } from './Popover';

/**
 * Clicking the active tab reveals the document itself: its name (editable
 * while it is not tied to a file), its format, and — stated plainly — where
 * its words currently live.
 */

export const CLOUD_LINES: Record<CloudDocState, string> = {
  local: '',
  synced: 'In your cloud library · synced',
  syncing: 'In your cloud library · syncing…',
  pending: 'In your cloud library · changes waiting to sync',
  offline: 'In your cloud library · will sync when you’re online',
  'signed-out': 'In a cloud library · sign in to sync',
  blocked: 'In your cloud library · can’t sync right now',
  error: 'In your cloud library · will retry',
};

function whereLine(doc: DocMeta, writesInPlace: boolean, ephemeral: boolean): string {
  const kept = ephemeral ? 'only in this window' : 'kept in this browser';
  switch (saveStatus(doc)) {
    case 'cloud':
      return 'Not saved to a file · Save As to make a copy on this device';
    case 'cloud-pending':
      return `Changes ${kept} until the cloud has them`;
    case 'blank':
      return 'New document · not saved to a file';
    case 'draft':
      return `Draft · ${kept} until you save it`;
    case 'edited':
      return doc.origin === 'file' ? `Unsaved changes · ${kept}` : `Changes ${kept} · saving downloads a copy`;
    case 'saved':
      return 'Saved to the file on disk';
    case 'unchanged':
      return writesInPlace ? 'Opened as a copy · saving asks where to save' : 'Opened as a copy · saving downloads a file';
    case 'downloaded':
      return 'Downloaded as a file · no unsaved changes';
  }
}

export function DocumentCard({ id, onDone }: { id: string; onDone: () => void }) {
  const app = useApp();
  const doc = useSession((s) => s.docs.find((d) => d.id === id));
  const ephemeral = useUi((s) => s.ephemeral);
  const [words] = useState(() => countWords(app.text(id)));
  const [draftName, setDraftName] = useState<string | null>(null);
  const auth = useCloudAuth();
  const sync = useCloudSync();
  if (!doc) return null;
  const signedIn = auth.status === 'signed-in' && !!sync && sync.access !== 'denied';
  const cloudState: CloudDocState | null = doc.cloud ? (doc.cloud.account !== auth.user?.id ? 'signed-out' : (sync?.docs[doc.id] ?? 'pending')) : null;

  const { base, ext } = splitName(doc.name);
  const renamable = doc.origin !== 'file';
  const writesInPlace = app.writesInPlace;

  const commit = () => {
    if (draftName !== null && draftName !== base) app.rename(doc.id, draftName);
    setDraftName(null);
  };

  const nameKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
      onDone();
      app.focusEditor();
    } else if (e.key === 'Escape' && draftName !== null && draftName !== base) {
      e.stopPropagation();
      setDraftName(null);
    }
  };

  const act = (fn: () => unknown) => () => {
    commit();
    onDone();
    fn();
  };

  return (
    <div className="doc-card">
      <div className="doc-name">
        {renamable ? (
          <label className="doc-name-field">
            <span className="visually-hidden">Name</span>
            <input
              data-autofocus
              value={draftName ?? base}
              spellCheck={false}
              autoComplete="off"
              onChange={(e) => setDraftName(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              onBlur={commit}
              onKeyDown={nameKey}
              size={Math.max(4, Math.min(28, (draftName ?? base).length + 1))}
            />
          </label>
        ) : (
          <span className="doc-name-text">{base}</span>
        )}
        <span className="doc-name-ext">{ext}</span>
      </div>
      <p className="doc-where">{whereLine(doc, writesInPlace, ephemeral)}</p>
      {cloudState && (
        <p className="doc-where doc-cloud" data-cloud={cloudState}>
          {CLOUD_LINES[cloudState]}
        </p>
      )}

      <div className="doc-meta-row">
        {renamable ? (
          <div className="segmented" role="radiogroup" aria-label="Format">
            {(['md', 'txt'] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={doc.kind === k}
                className="segment"
                onClick={() => app.setKind(doc.id, k)}
              >
                {k === 'md' ? 'Markdown' : 'Plain text'}
              </button>
            ))}
          </div>
        ) : (
          <span className="doc-kind">{doc.kind === 'md' ? 'Markdown' : 'Plain text'}</span>
        )}
        <span className="doc-words">
          {words.toLocaleString()} {words === 1 ? 'word' : 'words'}
        </span>
      </div>

      <div className="menu-sep" role="separator" />
      <div className="menu" role="menu" aria-label="Document actions" onKeyDown={menuKeys}>
        <MenuItem label="Save" hint={shortcutHint('Mod-s')} disabled={!doc.dirty && doc.origin === 'file'} onSelect={act(() => app.save(doc.id))} />
        <MenuItem label="Save As…" hint={shortcutHint('Mod-Shift-s')} onSelect={act(() => app.saveAs(doc.id))} />
        {doc.cloud && doc.cloud.account === auth.user?.id && (
          <>
            <MenuItem label="Sync Now" onSelect={act(() => app.cloud.syncNow())} />
            <MenuItem label="Delete from Cloud…" onSelect={act(() => app.cloud.deleteFromCloud(doc.id))} />
          </>
        )}
        {!doc.cloud && signedIn && (
          <MenuItem
            label="Save to Cloud"
            onSelect={act(async () => {
              const [report] = await app.cloud.moveToCloud([doc.id]);
              if (report) notify(report.status === 'verified' ? `Saved “${doc.name}” to your cloud library.` : report.detail, report.status === 'failed' ? 'error' : 'info');
            })}
          />
        )}
        {!doc.cloud && !signedIn && app.cloud.configured && <MenuItem label="Sign In to Use the Cloud…" onSelect={act(() => openAccount())} />}
        <MenuItem label="Close" hint={shortcutHint('Mod-w')} onSelect={act(() => app.close(doc.id))} />
      </div>
    </div>
  );
}
